import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "mirex_consent_v1";

const defaultConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  personalization: false,
};

const CookieConsentContext = createContext({
  consent: defaultConsent,
  decided: false,
  showBanner: false,
  showPreferences: false,
  openBanner: () => {},
  openPreferences: () => {},
  closePreferences: () => {},
  acceptAll: () => {},
  rejectAll: () => {},
  savePreferences: () => {},
});

function pushGtagConsent(consent) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  gtag("consent", "update", {
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
    analytics_storage: consent.analytics ? "granted" : "denied",
    personalization_storage: consent.personalization ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
  window.dataLayer.push({ event: "cookie_consent_update", consent });
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(defaultConsent);
  const [decided, setDecided] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          const merged = { ...defaultConsent, ...parsed };
          setConsent(merged);
          setDecided(true);
          pushGtagConsent(merged);
          return;
        }
      }
    } catch (_) {}
    setShowBanner(true);
  }, []);

  const persist = useCallback((next) => {
    setConsent(next);
    setDecided(true);
    setShowBanner(false);
    setShowPreferences(false);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
    pushGtagConsent(next);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ necessary: true, analytics: true, marketing: true, personalization: true });
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ necessary: true, analytics: false, marketing: false, personalization: false });
  }, [persist]);

  const savePreferences = useCallback((partial) => {
    persist({ ...defaultConsent, ...partial, necessary: true });
  }, [persist]);

  const openBanner = useCallback(() => setShowBanner(true), []);
  const openPreferences = useCallback(() => setShowPreferences(true), []);
  const closePreferences = useCallback(() => setShowPreferences(false), []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        decided,
        showBanner,
        showPreferences,
        openBanner,
        openPreferences,
        closePreferences,
        acceptAll,
        rejectAll,
        savePreferences,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
